/// <amd-module name="Build/Client/Linking" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

namespace Routes {
    export const AgentQueues = "ms.vss-build-web.agent-queues-route";
    export const BuildResult = "ms.vss-build-web.ci-results-hub-route";
    export const CIEditor = "ms.vss-ciworkflow.ci-editor-route";
    export const YamlEditor = "ms.vss-build-web.ci-definition-designer-route";
    export const PipelinesHub = "ms.vss-build-web.pipelines-hub-route";
    export const DeletedPipelines = "ms.vss-build-web.deleted-pipelines-route";
    export const FolderView = "ms.vss-build-web.pipelines-folder-route";

    // This should be in WIT linking
    export const WitForms = "ms.vss-work-web.work-items-form-route";
    export const WitQuery = "ms.vss-work-web.query-route";

    // This should be in Project linking
    export const ProjectSettings = "ms.vss-admin-web.project-admin-hub-route";
}

export namespace RouteParameters {
    export const BuildId = "buildId";
    export const DefinitionId = "definitionId";
    export const DefinitionScope = "definitionScope";
    export const Project = "project";
    export const Team = "team";
    export const View = "view";
    export const Type = "type";
}

export namespace WITRouteParameters {
    export const Id = "id";
    export const Wiql = "wiql";
    export const queryPath = "query";
}

export namespace ProjectRouteParameters {
    export const AdminPivot = "adminPivot";
}

export interface IBuildLinkingService extends IVssService {
    getAgentQueueUrl(queueId: number, agentId?: number, projectName?: string, teamName?: string): string;
    getBuildUrl(
        buildId: number,
        viewName?: string,
        projectName?: string,
        teamName?: string,
        additionalRouteValues?: { [key: string]: string }
    ): string;
    getDefinitionEditUrl(definitionId: number, viewName?: string, projectName?: string, teamName?: string): string;
    getNewDefinitionEditUrl(path?: string): string;
    getYamlPipelineEditUrl(pipelineId: number, branchName?: string, projectName?: string, teamName?: string): string;
    getCloneDefinitionEditUrl(definitionId: number, projectName?: string, teamName?: string): string;
    getDefinitionUrl(definitionId?: number, routeId?: string, projectName?: string, teamName?: string): string;
    getFolderViewUrl(): string;
    getFolderUrl(path: string): string;
    getAbsoluteUrl(url: string): string;
    getWorkItemUrl(workItemId: string | number, projectName?: string, teamName?: string): string;
    getWorkItemQueryUrl(wiql: string, projectName?: string, teamName?: string): string;
    getProjectSettingsUrl(projectName?: string, teamName?: string): string;
    getPipelinesUrl(definitionId: number, repositoryFilter?: number, branchFilter?: number, projectName?: string, teamName?: string): string;
    getDeletedPipelinesUrl(projectName?: string, teamName?: string): string;
    getPipelinesFolderUrl(projectName?: string, teamName?: string, folderPath?: string): string;
}

class BuildLinkingService extends VssService implements IBuildLinkingService {
    public getAgentQueueUrl(queueId: number, agentId?: number, projectName?: string, teamName?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        let url = routeUrl(this.pageContext, Routes.AgentQueues, routeValues);
        url += `?queueId=${queueId}`;
        if (agentId) {
            url += `&agentId=${agentId}`;
        }

        return url;
    }

    public getDefinitionEditUrl(definitionId: number, viewName?: string, projectName?: string, teamName?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        let url = routeUrl(this.pageContext, Routes.CIEditor, routeValues);
        url += `?_a=edit-build-definition&id=${definitionId}`;
        if (viewName) {
            url += `&${RouteParameters.View}=${viewName}`;
        }
        return url;
    }

    public getYamlPipelineEditUrl(pipelineId: number, branchName?: string, projectName?: string, teamName?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        routeValues["pipelineId"] = pipelineId.toString();
        if (branchName) {
            routeValues["branch"] = branchName;
        }

        return routeUrl(this.pageContext, Routes.YamlEditor, routeValues);
    }

    public getNewDefinitionEditUrl(path?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues);

        let url = routeUrl(this.pageContext, Routes.CIEditor, routeValues);
        url += "?_a=build-definition-getting-started";
        if (path) {
            url += "&path=" + encodeURIComponent(path);
        }
        return url;
    }

    public getCloneDefinitionEditUrl(definitionId: number, projectName?: string, teamName?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        let url = routeUrl(this.pageContext, Routes.CIEditor, routeValues);
        url += `?_a=clone-build-definition&id=${definitionId}`;
        return url;
    }

    public getDefinitionUrl(definitionId?: number, routeId?: string, projectName?: string, teamName?: string): string {
        routeId = routeId || Routes.PipelinesHub;
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        if (definitionId) {
            routeValues[RouteParameters.DefinitionId] = definitionId + "";
        }

        let url = routeUrl(this.pageContext, routeId, routeValues);
        url += definitionId ? "&_a=summary" : "";
        return url;
    }

    public getFolderViewUrl(): string {
        const routeId = Routes.PipelinesHub;
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, undefined, undefined);
        let url = routeUrl(this.pageContext, routeId, routeValues);
        url += "?view=folders";
        return url;
    }

    public getPipelinesUrl(definitionId: number, repositoryFilter?: number, branchFilter?: number, projectName?: string, teamName?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        if (definitionId) {
            routeValues[RouteParameters.DefinitionId] = definitionId + "";
        }

        let url = routeUrl(this.pageContext, Routes.PipelinesHub, routeValues);
        url += "&_a=summary";
        url += repositoryFilter ? "&repositoryFilter=" + repositoryFilter : "";
        url += branchFilter ? "&branchFilter=" + branchFilter : "";

        return url;
    }

    public getBuildUrl(
        buildId: number,
        viewName?: string,
        projectName?: string,
        teamName?: string,
        additionalRouteValues?: { [key: string]: string }
    ): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        routeValues[RouteParameters.BuildId] = buildId + "";

        let url = routeUrl(this.pageContext, Routes.BuildResult, routeValues);
        if (viewName) {
            url += `&${RouteParameters.View}=${viewName}`;
        }

        if (additionalRouteValues) {
            Object.keys(additionalRouteValues).forEach(key => {
                const value = additionalRouteValues[key];
                if (value) {
                    url += `&${key}=${additionalRouteValues[key]}`;
                }
            });
        }

        return url;
    }

    public getFolderUrl(path: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues);

        let url = routeUrl(this.pageContext, Routes.PipelinesHub, routeValues);
        if (path) {
            url += "?definitionScope=" + encodeURIComponent(path);
        }
        return this.getAbsoluteUrl(url);
    }

    public getWorkItemUrl(workitemId: string | number, projectName?: string, teamName?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        routeValues[WITRouteParameters.Id] = workitemId + "";

        return routeUrl(this.pageContext, Routes.WitForms, routeValues);
    }

    public getWorkItemQueryUrl(wiql: string, projectName?: string, teamName?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        let url = routeUrl(this.pageContext, Routes.WitQuery, routeValues) + "/" + WITRouteParameters.queryPath;
        if (wiql) {
            url += "?" + WITRouteParameters.Wiql + "=" + encodeURIComponent(wiql);
        }

        return this.getAbsoluteUrl(url);
    }

    public getProjectSettingsUrl(projectName?: string, teamName?: string): string {
        const routeId = Routes.ProjectSettings;

        // Set route values
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues);
        delete routeValues[RouteParameters.Team]; // Remove team route value if it exists
        routeValues[ProjectRouteParameters.AdminPivot] = "projectOverview";

        const url = routeUrl(this.pageContext, routeId, routeValues);
        return url;
    }

    public getAbsoluteUrl(url: string) {
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
            url = combineUrlPaths(location.origin, url);
        }

        return url;
    }

    public getDeletedPipelinesUrl(projectName?: string, teamName?: string) {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        const url = routeUrl(this.pageContext, Routes.DeletedPipelines, routeValues);
        return url;
    }

    public getPipelinesFolderUrl(projectName?: string, teamName?: string, folderPath?: string): string {
        const routeValues: { [key: string]: string } = {};
        this._initializeRouteValues(routeValues, projectName, teamName);

        if (folderPath) {
            routeValues[RouteParameters.DefinitionScope] = folderPath;
        }

        const url = routeUrl(this.pageContext, Routes.FolderView, routeValues);
        return url;
    }

    private _initializeRouteValues(routeValues: { [key: string]: string }, projectName?: string, teamName?: string) {
        if (!projectName || !teamName) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            if (tfsPageData) {
                if (!projectName && tfsPageData.project) {
                    projectName = tfsPageData.project.name;
                }
                const teamData = tfsPageService.getHorizontalNavigationTeamData();
                if (!teamName && teamData) {
                    teamName = teamData.name;
                }
            }
        }

        if (projectName) {
            routeValues[RouteParameters.Project] = projectName;
        }

        if (teamName) {
            routeValues[RouteParameters.Team] = teamName;
        }
    }
}

Services.add("IBuildLinkingService", { serviceFactory: BuildLinkingService });
